import { useEffect, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import {
  CircleCheckIcon,
  CopyIcon,
  EllipsisVerticalIcon,
  OctagonAlertIcon,
  SparklesIcon,
  Trash2Icon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { EditableText } from '@/components/atoms/EditableText/EditableText';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { StyledBadge } from '@/components/atoms/StyledBadge/StyledBadge';
import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import { getVisitorStatus } from '@/modules/humanChat/components/Conversation/MessageArea/helpers';
import { ConversationViewing } from '@/modules/humanChat/components/ConversationViewing/ConversationViewing';
import {
  useConversationDetail,
  useConversationMutations,
} from '@/modules/humanChat/hooks/useConversations';
import { endpoints as convCountsEndpoints } from '@/modules/humanChat/hooks/useConversationsCounts';
import { useSummarize } from '@/modules/humanChat/hooks/useSummarize';
import { popModal, pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { setChannelUserId, setSessionId } from '@/redux/session/actions';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { LENGTH_XS } from '@/util/validation/constants';
import { getEditableTextValidationSchema } from '@/util/validation/schema-builders';

const ConversationHeader = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { sessionId, slug } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deskId = useDeskId();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );
  const { conversation, getStatus } = useConversationDetail({ deskId, sessionId });
  const { updateConversation, deleteConversation } = useConversationMutations({
    deskId,
    sessionId,
  });
  const { summarize, isPending: isSummarizing } = useSummarize(deskId, sessionId);

  const channel_user_id = conversation?.context?.user?.[`${conversation?.channel}_psid`];

  const { visitorStatus, lastVisitorActivity } = getVisitorStatus(conversation);
  const displayName = conversation?.context?.user?.display_name ?? t('common.anonymous');

  const validationSchema = getEditableTextValidationSchema(
    LENGTH_XS, // 32 characters max
    [], // No restricted values
    t('common.display_name') // Label for error messages
  );

  const updateDisplayName = useCallback(
    (newDisplayName: string) => {
      updateConversation({ context: { user: { display_name: newDisplayName } } });
    },
    [updateConversation]
  );

  const clickTrash = useCallback(() => {
    updateConversation(
      { status: conversation?.status === 'trash' ? 'closed' : 'trash' },
      {
        onSuccess: () => {
          toast.success(
            conversation?.status === 'trash'
              ? t('conversation.removed_from_trash')
              : t('conversation.moved_to_trash')
          );
        },
      }
    );
  }, [conversation?.status, t, updateConversation]);

  const clickDeletePermanently = useCallback(() => {
    const modalProps = {
      subtitle: t('conversation.trash_warning'),
      confirm: false,
      onDelete: () => {
        deleteConversation(sessionId, {
          onSuccess: () => {
            navigate(`/${slug}/chats/${deskId}/conversations`);
            dispatch(popModal());
            queryClient.invalidateQueries({ queryKey: [convCountsEndpoints.counts(deskId)] });
          },
        });
      },
    };
    dispatch(pushModal(MODAL_DELETE, modalProps));
  }, [deleteConversation, deskId, dispatch, navigate, queryClient, sessionId, slug, t]);

  const clickSpam = useCallback(() => {
    updateConversation(
      { status: conversation?.status === 'spam' ? 'closed' : 'spam' },
      {
        onSuccess: () => {
          toast.success(
            conversation?.status === 'spam'
              ? t('conversation.removed_from_spam')
              : t('conversation.move_to_spam')
          );
        },
      }
    );
  }, [updateConversation, conversation?.status, t]);

  const clickSummarize = useCallback(() => {
    if (sessionId && deskId) {
      trackEvent(EventName.ClickConversationSummarize, { session_id: sessionId });
      summarize({ sessionId, deskId });
    }
  }, [sessionId, deskId, summarize]);

  const clickCopyName = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(displayName);
      toast.success(t('common.copied_name_to_clipboard'));
    } catch {
      toast.error(t('common.copy_failed'));
    }
  }, [displayName, t]);

  useEffect(() => {
    if (channel_user_id) {
      dispatch(setChannelUserId(channel_user_id));
    }
    return () => {
      dispatch(setChannelUserId(null));
    };
  }, [dispatch, channel_user_id]);

  useEffect(() => {
    if (sessionId) {
      dispatch(setSessionId(sessionId));
    }
    return () => {
      dispatch(setSessionId(null));
    };
  }, [dispatch, sessionId]);

  const visitorAvatar = (
    <StyledBadge
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      status={visitorStatus ?? undefined}
      invisible={!visitorStatus}
    >
      <Avatar size="sm">
        <AvatarImage src={conversation?.context?.user?.avatar} alt={displayName} />
        <AvatarFallback size="sm">{displayName?.[0] || '?'}</AvatarFallback>
      </Avatar>
    </StyledBadge>
  );

  return (
    <div className="relative top-0 z-10 flex h-14 w-full items-center border-b border-border bg-background px-2">
      <div className="flex w-full items-center gap-3">
        {getStatus === 'pending' ? (
          <Skeleton className="h-7 w-32.5" />
        ) : (
          <>
            <div className="flex items-center gap-2">
              {lastVisitorActivity ? (
                <InfoTooltip
                  content={t('conversation.last_visitors_activity', { 0: lastVisitorActivity })}
                  showArrow
                >
                  {visitorAvatar}
                </InfoTooltip>
              ) : (
                visitorAvatar
              )}
              <div className="group flex items-center gap-2">
                <EditableText<typeof validationSchema>
                  defaultValue={displayName}
                  onSubmit={updateDisplayName}
                  variant="!subheading-semi-bold"
                  canEdit={canWrite}
                  validationSchema={validationSchema}
                />
                {canWrite && (
                  <InfoTooltip content={t('common.copy')}>
                    <Button
                      variant="ghost-neutral"
                      size="icon-sm"
                      onClick={clickCopyName}
                      aria-label={t('common.copy')}
                      className="opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <CopyIcon size={14} />
                    </Button>
                  </InfoTooltip>
                )}
              </div>
            </div>
            {conversation?.context?.user?.verified && (
              <span className="max-w-7.5">
                <InfoTooltip content={t('contacts.verified_user')} showArrow>
                  <span>
                    <CircleCheckIcon className="text-success" size={14} />
                  </span>
                </InfoTooltip>
              </span>
            )}
          </>
        )}
        <div className="ml-auto flex items-center gap-1">
          <ConversationViewing />

          <InfoTooltip content={t('conversation.summary.button')}>
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              onClick={clickSummarize}
              disabled={!canWrite}
              isLoading={isSummarizing}
            >
              <SparklesIcon size={16} />
            </Button>
          </InfoTooltip>

          <DropdownMenu>
            <InfoTooltip content={t('common.options')}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost-neutral"
                  size="icon-sm"
                  disabled={!canWrite}
                  aria-label={t('common.see_more_options')}
                >
                  <EllipsisVerticalIcon size={20} />
                </Button>
              </DropdownMenuTrigger>
            </InfoTooltip>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={clickSpam}>
                <OctagonAlertIcon size={16} className="text-muted-foreground" />
                <span className="text-foreground">
                  {conversation?.status === 'spam'
                    ? t('conversation.remove_from_spam')
                    : t('conversation.report_spam')}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={clickTrash}>
                <Trash2Icon size={16} className="text-muted-foreground" />
                <span className="text-foreground">
                  {conversation?.status === 'trash'
                    ? t('conversation.remove_from_trash')
                    : t('conversation.move_to_trash')}
                </span>
              </DropdownMenuItem>
              {conversation?.status === 'trash' && (
                <DropdownMenuItem onClick={clickDeletePermanently}>
                  <Trash2Icon size={16} className="text-muted-foreground" />
                  <span className="text-foreground">{t('conversation.delete_permanently')}</span>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};

export default ConversationHeader;
